import {Injectable} from '@angular/core';
import {HttpContextToken, HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpResponse} from '@angular/common/http';
import {Observable, throwError} from 'rxjs';
import {catchError, map} from 'rxjs/operators';
import {VERSION_API_CURRENT, VERSION_UI_CURRENT, VersionService} from '../services/version.service';
import {SessionService} from '../services/session.service';
import {UtilsService} from '../services/utils.service';
import {ErrorReportingService} from '../services/error-reporting.service';


const AUTH_FAILURES_BEFORE_LOGOUT = 3;

/**
 * Lets a caller mark specific error responses as an expected outcome (e.g. a 404 meaning
 * "not found yet" rather than a bug) so AppInterceptor doesn't report them to error tracking.
 */
export const SKIP_ERROR_REPORTING = new HttpContextToken<(error: HttpErrorResponse) => boolean>(() => () => false);


@Injectable()
export class AppInterceptor implements HttpInterceptor {
  constructor(
    private versionService: VersionService,
    private sessionService: SessionService,
    private errorReportingService: ErrorReportingService,
  ) {
  }


  private authFailureCount: number = 0;


  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const bearerToken: string = localStorage.getItem('v2-bearerToken');

    if (bearerToken) {
      request = request.clone({
        headers: request.headers
          .set('Authorization', 'Bearer ' + bearerToken)
          .set('x-motion-ui-version', '' + VERSION_UI_CURRENT)
          .set('x-motion-api-version', '' + VERSION_API_CURRENT),
      });
    }

    return next.handle(request).pipe(
      map((event: HttpEvent<any>) => {
        if (event instanceof HttpResponse) {
          let version = event.headers.get('x-motion-ui-version');

          if (version) {
            this.versionService.setLatestVersion(version);
          }

          this.authFailureCount = 0;
        }

        return event;
      }),
      catchError((err) => {
        const errorMessage = UtilsService.extractErrorMessage(err?.error);
        const isAuthError = errorMessage.startsWith('authentication failed') ||
                           errorMessage.startsWith('authentication header malformed');

        if (isAuthError) {
          this.authFailureCount++;
        }

        const forcedLogout = this.authFailureCount > AUTH_FAILURES_BEFORE_LOGOUT;

        if (forcedLogout) {
          this.sessionService.logOut();
        }

        const isExpectedFailure = request.context.get(SKIP_ERROR_REPORTING)(err);

        // Skip routine auth failures (expected as sessions expire) only report once they force a logout, since that's the signal worth alerting on. Also skip failures a caller has explicitly marked as expected via SKIP_ERROR_REPORTING.
        if ((!isAuthError || forcedLogout) && !isExpectedFailure) {
          this.errorReportingService.reportError(new Error(errorMessage), {url: request.url, status: err?.status});
        }

        return throwError(err);
      }),
    );
  }
}
